import { Add, Cancel, Delete, Done, Edit, Undo } from "@mui/icons-material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasePriceApiService from "../../../adapters/xhr/BasePriceApiService";
import authService from "../../../components/api-authorization/AuthorizeService";
import { MyNumberField } from "../../../components/my-number-field/MyNumberField";
import { UserRoles } from "../../../Constant";
import SnackbarUtils from "../../../SnackbarUtils";

export const BasePriceSection = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [previousRows, setPreviousRows] = useState([]);
  const isReadOnly = authService._user?.role == UserRoles.CaseManager;
  const hasAddRow = rows.some((item) => item.isAddMode);
  const hasEditRow = rows.some((item) => item.isEditMode);

  const columns = [
    { id: "Year", label: t("year"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    { id: "Actions", label: t("actions"), minWidth: 170, hidden: isReadOnly },
  ];

  useEffect(() => {
    fetchSettingsAsync();
  }, []);

  const fetchSettingsAsync = async () => {
    const response = await BasePriceApiService.getAll();
    if (response?.isSuccess) {
      const data = response.data
        .map((item) => ({
          id: item.id,
          Year: item.year,
          Value: item.value,
        }))
        .sort((a, b) => b.Year - a.Year);

      setRows(data);
      setPreviousRows(data);
    }
  };

  const onAddRow = () => {
    setRows([{ id: "0", Year: "", Value: "", isAddMode: true }, ...previousRows]);
  };

  const onEditRow = (id) => {
    setRows(
      previousRows.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: true };
        }

        return row;
      })
    );
  };

  const onChangeRowValues = (e, row) => {
    const value = e.target.value;
    if (value < 0) {
      return;
    }

    const name = e.target.name;

    setRows(
      rows.map((item) => {
        if (item.id === row.id) {
          return { ...item, [name]: value };
        }

        return item;
      })
    );
  };

  const onRevertChanges = () => {
    setRows(previousRows);
  };

  const onSaveSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);

    if (row.Year?.trim() === "" || row.Value?.trim() === "") {
      return;
    }

    const payload = {
      year: Number(row.Year),
      value: Number(row.Value),
    };

    const response = await BasePriceApiService.update(row.Year, payload);
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(t(response?.errorMessage)));
      onRevertChanges();
      return;
    }

    await fetchSettingsAsync();
  };

  const onDeleteSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);
    const response = await BasePriceApiService.deleteByYear(row.Year);
    if (!response?.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage));
      return;
    }

    await fetchSettingsAsync();
  };

  const onAddSettingsAsync = async (id) => {
    const row = rows.find((item) => item.id === id);
    
    if (rows.find((item) => item.id != row.id && item.Year == row.Year) != null) {
      SnackbarUtils.error(t("base_price_for_year_existed", { year: row.Year }));
      return;
    }

    if (row.Year?.trim() === "" || row.Value?.trim() === "") {
      return;
    }

    const payload = {
      year: Number(row.Year),
      value: Number(row.Value),
    };

    const response = await BasePriceApiService.add(payload);
    if (!response?.isSuccess) {
      SnackbarUtils.error(t(response?.errorMessage));
      return;
    }

    await fetchSettingsAsync();
  };

  return (
    <>
      <Toolbar> 
        <Typography variant="h6" id="tableTitle" component="div">
          {t("base_price")}
        </Typography>
        {!hasAddRow && !isReadOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onAddRow()} />}
        {hasAddRow && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertChanges()} />}
      </Toolbar>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns
                .filter((item) => !item.hidden)
                .map((column, key) => (
                  <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                    {column.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, key) => {
                return (
                  <TableRow hover tabIndex={-1} key={key}>
                    {columns
                      .filter((item) => !item.hidden)
                      .map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && !hasEditRow && !hasAddRow && (
                                <>
                                  <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onEditRow(row.id)} />
                                  <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteSettingsAsync(row.id)} />
                                </>
                              )}
                              {row.isEditMode && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveSettingsAsync(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertChanges()} />
                                </>
                              )}
                              {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddSettingsAsync(row.id)} />}
                            </TableCell>
                          );
                        }

                        const value = row[column.id];

                        if (row.isAddMode || (row.isEditMode && column.editable)) {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <MyNumberField name={column.id} size="small" value={value} onChange={(e) => onChangeRowValues(e, row)} />
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.format && typeof value === "number" ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};